import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Camera, Upload, Loader2, Trash2, Download, Check } from 'lucide-react';
import { toast } from 'sonner';
import { projectFilesApi } from '../api';
import { fmtDate } from '../constants';

export const FileThumb = ({ file, className = '' }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objectUrl;
    projectFilesApi.fileBlob(file.id).then((r) => {
      objectUrl = URL.createObjectURL(r.data);
      setUrl(objectUrl);
    }).catch(() => {});
    return () => objectUrl && URL.revokeObjectURL(objectUrl);
  }, [file.id]);
  return url ? (
    <img src={url} alt={file.caption || file.original_filename} className={`object-cover ${className}`} />
  ) : (
    <div className={`flex items-center justify-center bg-[#1F1F1F] ${className}`}>
      <Loader2 size={16} className="animate-spin text-gray-600" />
    </div>
  );
};

export const downloadProjectFile = async (file) => {
  const r = await projectFilesApi.fileBlob(file.id);
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.original_filename || 'file';
  a.click();
  URL.revokeObjectURL(url);
};

const PhotoLightbox = ({ photo, onOpenChange, onSaved, onDelete, locked }) => {
  const [url, setUrl] = useState(null);
  const [caption, setCaption] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let objectUrl;
    if (photo) {
      setCaption(photo.caption || '');
      projectFilesApi.fileBlob(photo.id).then((r) => {
        objectUrl = URL.createObjectURL(r.data);
        setUrl(objectUrl);
      }).catch(() => {});
    } else setUrl(null);
    return () => objectUrl && URL.revokeObjectURL(objectUrl);
  }, [photo]);

  const saveCaption = async () => {
    setSaving(true);
    try {
      const res = await projectFilesApi.update(photo.id, { caption: caption.trim() || null });
      toast.success('Caption saved');
      onSaved?.(res.data);
    } catch { toast.error('Could not save caption'); } finally { setSaving(false); }
  };

  if (!photo) return null;
  return (
    <Dialog open={Boolean(photo)} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111111] border-gray-800 text-white w-[calc(100vw-2rem)] max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="photo-lightbox">
        <DialogHeader>
          <DialogTitle className="text-base break-all">{photo.caption || photo.original_filename}</DialogTitle>
          <DialogDescription className="text-gray-500">
            Uploaded {fmtDate(photo.created_at?.slice(0, 10))} by {photo.created_by_username}
          </DialogDescription>
        </DialogHeader>
        {url ? (
          <img src={url} alt={photo.caption || photo.original_filename} className="max-h-[60vh] w-auto mx-auto rounded-md" />
        ) : (
          <div className="text-center py-16 text-gray-500"><Loader2 size={24} className="mx-auto animate-spin text-[#D4AF37]" /></div>
        )}
        <div className="flex flex-wrap gap-2 items-center pt-2">
          {!locked && (
            <>
              <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Add a caption…" className="bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37] flex-1 min-w-[160px]" data-testid="photo-caption-input" />
              <Button onClick={saveCaption} disabled={saving} variant="outline" className="border-gray-700 text-gray-300" data-testid="photo-caption-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
              </Button>
            </>
          )}
          <Button onClick={() => downloadProjectFile(photo)} variant="outline" className="border-gray-700 text-gray-300" data-testid="photo-download-btn">
            <Download size={14} /> Download
          </Button>
          {!locked && (
            <Button onClick={() => onDelete(photo)} variant="outline" className="border-gray-700 text-gray-300 hover:text-red-400 hover:border-red-500" data-testid="photo-delete-btn">
              <Trash2 size={14} /> Delete
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ProjectPhotosTab = ({ project }) => {
  const fileRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const locked = Boolean(project.is_archived);

  const load = useCallback(() => {
    projectFilesApi.list({ project_id: project.id, kind: 'photo' })
      .then((r) => setPhotos(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('project_id', project.id);
      fd.append('kind', 'photo');
      try { await projectFilesApi.upload(fd); ok += 1; } catch { fail += 1; }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} photo${ok === 1 ? '' : 's'} uploaded`);
    if (fail) toast.error(`${fail} photo${fail === 1 ? '' : 's'} failed (JPEG/PNG/WebP up to 15 MB)`);
    load();
  };

  const handleDelete = async (photo) => {
    try {
      await projectFilesApi.delete(photo.id);
      toast.success('Photo deleted');
      setSelected(null);
      load();
    } catch { toast.error('Could not delete photo'); }
  };

  return (
    <div data-testid="project-photos-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Project Photos</h3>
          <p className="text-sm text-gray-500">Before, during, and after shots for this project.</p>
        </div>
        {!locked && (
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="photos-upload-btn">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload Photos
          </Button>
        )}
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleFiles} className="hidden" data-testid="photos-file-input" />
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500" data-testid="photos-loading">
          <Loader2 size={24} className="mx-auto mb-2 animate-spin text-[#D4AF37]" /> Loading photos…
        </div>
      ) : photos.length === 0 ? (
        <Card className="bg-[#111111] border-gray-800 p-12 text-center" data-testid="photos-empty">
          <Camera size={32} className="mx-auto mb-3 text-gray-700" />
          <p className="text-white font-medium mb-1">No photos yet</p>
          <p className="text-gray-500 text-sm">Upload job site photos to document this project's progress.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="photos-grid">
          {photos.map((p) => (
            <button key={p.id} onClick={() => setSelected(p)} className="group text-left" data-testid={`photo-card-${p.id}`}>
              <FileThumb file={p} className="w-full aspect-square rounded-lg border border-gray-800 group-hover:border-[#D4AF37]/60 transition-colors" />
              <div className="text-[11px] text-gray-500 mt-1 truncate">{p.caption || fmtDate(p.created_at?.slice(0, 10))}</div>
            </button>
          ))}
        </div>
      )}

      <PhotoLightbox
        photo={selected}
        onOpenChange={(o) => !o && setSelected(null)}
        onSaved={(updated) => { setSelected(updated); load(); }}
        onDelete={handleDelete}
        locked={locked}
      />
    </div>
  );
};
